'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  alt: string;
  isVeg: boolean;
}

interface CartReviewProps {
  items: CartItem[];
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemoveItem: (itemId: string) => void;
}

const DELIVERY_FEE_THRESHOLD = 200;
const DELIVERY_FEE = 30;

const CartReview = ({ items, onUpdateQuantity, onRemoveItem }: CartReviewProps) => {
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const tax = subtotal * 0.05;
  const deliveryFee = subtotal > 0 && subtotal < DELIVERY_FEE_THRESHOLD ? DELIVERY_FEE : 0;
  const total = subtotal + tax + deliveryFee;

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    onUpdateQuantity(itemId, newQuantity);
  };

  return (
    <div className="rounded-lg bg-card shadow-md">
      <div className="border-b border-border px-6 py-4">
        <h2 className="font-heading text-xl font-semibold text-foreground">
          Order Summary ({items.length} {items.length === 1 ? 'item' : 'items'})
        </h2>
      </div>

      <div className="max-h-[400px] overflow-y-auto px-6 py-4">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Icon name="ShoppingCartIcon" size={48} className="text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground">Your cart is empty</p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <div
                key={item.id}
                className="tilt-card flex space-x-4 rounded-lg border border-border bg-background p-4 transition-smooth hover:shadow-sm"
              >
                <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-md">
                  <AppImage
                    src={item.image}
                    alt={item.alt}
                    className="h-full w-full object-cover"
                  />
                  <div
                    className={`absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full ${
                      item.isVeg ? 'bg-success' : 'bg-error'
                    }`}
                  >
                    <div className="h-2 w-2 rounded-full bg-white"></div>
                  </div>
                </div>

                <div className="flex flex-1 flex-col">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-foreground">{item.name}</h3>
                      <p className="text-data mt-1 text-sm font-semibold text-primary">
                        ₹{item.price.toFixed(2)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRemoveItem(item.id)}
                      className="rounded-md p-1 text-muted-foreground transition-smooth hover:bg-muted hover:text-error focus-ring"
                      aria-label={`Remove ${item.name} from cart`}
                    >
                      <Icon name="TrashIcon" size={18} />
                    </button>
                  </div>

                  <div className="mt-3 flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                      className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-foreground transition-smooth hover:bg-muted focus-ring disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label="Decrease quantity"
                    >
                      <Icon name="MinusIcon" size={16} />
                    </button>
                    <span className="text-data w-12 text-center text-sm font-medium text-foreground">
                      {item.quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                      className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-foreground transition-smooth hover:bg-muted focus-ring"
                      aria-label="Increase quantity"
                    >
                      <Icon name="PlusIcon" size={16} />
                    </button>
                    <span className="ml-2 text-data text-sm font-medium text-foreground">
                      ₹{(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="border-t border-border bg-muted px-6 py-4">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>Subtotal</span>
              <span className="text-data font-medium">₹{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Tax (5%)</span>
              <span className="text-data font-medium">₹{tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Delivery Fee</span>
              <span className="text-data font-medium">₹{deliveryFee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t border-border pt-2 text-base font-semibold text-foreground">
              <span>Total</span>
              <span className="text-data text-lg">₹{total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartReview;
